import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import sessionManager from '../utils/sessionManager';

/**
 * FastProtectedRoute Component
 * 
 * Ultra-fast synchronous authentication and role-based access control.
 * Eliminates async operations and loading states for instant rendering.
 * 
 * Props:
 * - children: The component to render if access is granted
 * - requiredRole: The role required to access this route ('student', 'admin', 'agent', 'sub-admin')
 * - allowedRoles: Array of roles that can access this route (alternative to requiredRole)
 * - redirectTo: Custom redirect path (defaults to '/login')
 */
const FastProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  allowedRoles = [], 
  redirectTo = '/login' 
}) => {
  const location = useLocation();

  // Memoized authentication check - runs synchronously
  const authResult = useMemo(() => {
    const startTime = performance.now();
    console.log('[FastProtectedRoute] Starting instant authentication check...');
    
    try {
      // Fast synchronous authentication check
      const userData = localStorage.getItem('userData');
      const token = localStorage.getItem('token');
      
      // Quick validation
      if (!userData || !token) {
        console.log('FastProtectedRoute: No authentication data');
        return { authorized: false, reason: 'No authentication data' };
      }

      let user;
      try {
        user = JSON.parse(userData);
      } catch (parseError) {
        console.error('FastProtectedRoute: Parse error:', parseError);
        // Clean up corrupted data
        localStorage.removeItem('userData');
        localStorage.removeItem('token');
        toast.error('Session data corrupted. Please log in again.');
        return { authorized: false, reason: 'Corrupted data' };
      }
      
      // Structure validation
      if (!user.email || !user.role) {
        console.log('FastProtectedRoute: Invalid user structure');
        localStorage.removeItem('userData');
        localStorage.removeItem('token');
        toast.error('Session expired. Please log in again.');
        return { authorized: false, reason: 'Invalid structure' };
      }

      // Role validation
      const validRoles = ['student', 'agent', 'admin', 'sub_admin_usa', 'sub_admin_aus_nz', 'sub_admin_uk', 'sub_admin_canada', 'sub_admin_europe'];
      if (!validRoles.includes(user.role)) {
        console.log(`FastProtectedRoute: Invalid role: ${user.role}`);
        localStorage.removeItem('userData');
        localStorage.removeItem('token');
        toast.error('Session data corrupted. Please log in again.');
        return { authorized: false, reason: 'Invalid role' };
      }

      // Role authorization check
      const userRole = user.role.toLowerCase();
      
      if (requiredRole) {
        const normalizedRequiredRole = requiredRole.toLowerCase().replace(/-/g, '_');
        const normalizedUserRole = userRole.replace(/-/g, '_');
        
        if (normalizedUserRole === normalizedRequiredRole) {
          console.log(`FastProtectedRoute: Access granted for role ${userRole}`);
          
          // Start session monitoring (non-blocking)
          setTimeout(() => {
            if (!sessionManager.isUserLoggedIn() || !sessionManager.sessionTimer) {
              sessionManager.startSession();
            }
            sessionManager.extendSession();
          }, 0);
          
          const endTime = performance.now();
          console.log(`[FastProtectedRoute] Auth check completed in ${(endTime - startTime).toFixed(2)}ms`);
          
          return { authorized: true };
        } else {
          console.log(`FastProtectedRoute: Role mismatch. Required: ${requiredRole}, User: ${userRole}`);
          toast.error(`Access denied! This page requires ${requiredRole} access.`);
          return { authorized: false, reason: 'Role mismatch' };
        }
      } else if (allowedRoles.length > 0) {
        const normalizedAllowedRoles = allowedRoles.map(role => role.toLowerCase().replace(/-/g, '_'));
        const normalizedUserRole = userRole.replace(/-/g, '_');
        
        if (normalizedAllowedRoles.includes(normalizedUserRole)) {
          console.log(`FastProtectedRoute: Access granted for role ${userRole}`);
          
          // Start session monitoring (non-blocking)
          setTimeout(() => {
            if (!sessionManager.isUserLoggedIn() || !sessionManager.sessionTimer) {
              sessionManager.startSession();
            }
            sessionManager.extendSession();
          }, 0);
          
          const endTime = performance.now();
          console.log(`[FastProtectedRoute] Auth check completed in ${(endTime - startTime).toFixed(2)}ms`);
          
          return { authorized: true };
        } else {
          console.log(`FastProtectedRoute: Role not in allowed list. Allowed: ${allowedRoles.join(', ')}, User: ${userRole}`);
          toast.error(`Access denied! This page requires one of: ${allowedRoles.join(', ')}`);
          return { authorized: false, reason: 'Role not allowed' };
        }
      } else {
        // No role restrictions
        console.log('FastProtectedRoute: Access granted (no role restrictions)');
        
        // Start session monitoring (non-blocking)
        setTimeout(() => {
          if (!sessionManager.isUserLoggedIn() || !sessionManager.sessionTimer) {
            sessionManager.startSession();
          }
          sessionManager.extendSession();
        }, 0);
        
        const endTime = performance.now();
        console.log(`[FastProtectedRoute] Auth check completed in ${(endTime - startTime).toFixed(2)}ms`);
        
        return { authorized: true };
      }
      
    } catch (error) {
      console.error('FastProtectedRoute: Unexpected error:', error);
      toast.error('Authentication error. Please log in again.');
      return { authorized: false, reason: 'Unexpected error' };
    }
  }, [requiredRole, allowedRoles, location.pathname]);

  // Immediate redirect for unauthorized users - no loading state needed
  if (!authResult.authorized) {
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location, reason: authResult.reason }}
        replace 
      />
    );
  }

  // Render protected component immediately
  return children;
};

export default FastProtectedRoute;
